/**
 * Tool Photo Uploader Component
 * Handles photo capture, gallery selection, and AI-powered analysis
 */

import { useRef, useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Camera, ImagePlus, X, Loader2, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useParseToolPhoto } from '@/hooks/useToolbox';
import type { ParsedToolData } from '@/types/toolbox';
import { toast } from 'sonner';

interface ToolPhotoUploaderProps {
  value?: File | null;
  previewUrl?: string | null;
  onChange: (file: File | null) => void;
  onAnalysisComplete?: (data: ParsedToolData) => void;
  disabled?: boolean;
  className?: string;
}

export function ToolPhotoUploader({
  value,
  previewUrl,
  onChange,
  onAnalysisComplete,
  disabled,
  className,
}: ToolPhotoUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  
  const parsePhoto = useParseToolPhoto();
  const isAnalyzing = parsePhoto.isPending;

  const handleFileSelect = async (file: File | null) => {
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        return;
      }

      // Create local preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLocalPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      
      // Notify parent of file change
      onChange(file);

      // If we have an analysis callback, trigger AI analysis
      if (onAnalysisComplete) {
        try {
          const result = await parsePhoto.mutateAsync(file);
          
          // Check if we got any useful data
          const hasData = result.name || result.brand || result.model || 
                          result.part_number || result.serial_number;
          
          if (hasData) {
            toast.success('Photo analyzed - fields updated', {
              icon: <Sparkles className="h-4 w-4" />,
            });
            onAnalysisComplete(result);
          } else {
            toast.info("Couldn't detect tool info - please fill in manually");
          }
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to analyze photo';
          
          // Check for specific error types
          if (message.includes('Rate limit')) {
            toast.error('Please try again in a moment');
          } else if (message.includes('credits')) {
            toast.error('AI analysis unavailable');
          } else {
            toast.error("Couldn't analyze photo - fill in manually");
          }
        }
      }
    } else {
      setLocalPreview(null);
      onChange(null);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    handleFileSelect(file);
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const handleRemove = () => {
    setLocalPreview(null);
    onChange(null);
  };

  const displayUrl = localPreview || previewUrl;

  return (
    <div className={cn('space-y-3', className)}>
      {/* Preview Area */}
      <div className="aspect-square bg-muted rounded-lg border-2 border-dashed border-border relative overflow-hidden">
        {displayUrl ? (
          <>
            <img
              src={displayUrl}
              alt="Tool preview"
              className={cn(
                "w-full h-full object-cover transition-opacity",
                isAnalyzing && "opacity-50"
              )}
            />
            
            {/* Analysis Overlay */}
            {isAnalyzing && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-background/60 backdrop-blur-sm">
                <Loader2 className="h-8 w-8 animate-spin text-primary mb-2" />
                <p className="text-sm font-medium text-foreground">Scanning...</p>
                <p className="text-xs text-muted-foreground">Extracting tool info</p>
              </div>
            )}
            
            {!disabled && !isAnalyzing && (
              <Button
                type="button"
                variant="secondary"
                size="icon"
                className="absolute top-2 right-2 h-8 w-8 shadow-md"
                onClick={handleRemove}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground">
            <ImagePlus className="h-10 w-10 mb-2" />
            <p className="text-sm">Add a photo</p>
            <p className="text-xs mt-1">AI will extract tool info</p>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      {!disabled && (
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={() => cameraInputRef.current?.click()}
            disabled={isAnalyzing}
          >
            <Camera className="h-4 w-4 mr-2" />
            Camera
          </Button>
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={() => fileInputRef.current?.click()}
            disabled={isAnalyzing}
          >
            <ImagePlus className="h-4 w-4 mr-2" />
            Gallery
          </Button>
        </div>
      )}

      {/* Hidden File Inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  );
}
